import { useEffect } from 'react';

import { httpService } from '@flexo/general';
import { ALERT, CALENDAR, KPI, METADATA, MOCK_ENTITIES, store } from '@flexo/reducers';
import { DateTime } from 'luxon';
import { useDispatch } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { _navigate } from './helpers';

const ENV = (import.meta as any).env;

/**
 * One-shot calendar clamping after community data is loaded.
 * Ensures the calendar date falls within [startDate, min(endDate, today)].
 * Uses replaceState (not pushState) to avoid PostHog monkey-patch loop.
 */
function clampCalendarDateToEntity(community: any, dispatch: any) {
  if (!community) return;

  const now = DateTime.now().startOf('day');
  const calendarDate = (store.getState() as any).calendar?.hiveCalendar?.selectedDate?.[0];
  if (!calendarDate) return;

  const calendarDt = DateTime.fromJSDate(calendarDate).startOf('day');

  const entityEnd = community.endDate
    ? DateTime.fromISO(community.endDate).startOf('day')
    : null;
  const entityStart = community.startDate
    ? DateTime.fromISO(community.startDate).startOf('day')
    : null;

  // If endDate is missing, also check the entities list for this community
  let effectiveEndDt = entityEnd;
  if (!effectiveEndDt) {
    const entities = (store.getState() as any).metadata?.entities;
    const fromEntities = (entities?.community || entities?.communities || [])
      .find((c: any) => c.communityId === community.communityId);
    if (fromEntities?.endDate) {
      effectiveEndDt = DateTime.fromISO(fromEntities.endDate).startOf('day');
    }
  }

  // effectiveEnd = if endDate is in the past, use endDate; otherwise use today
  const effectiveEnd = effectiveEndDt && effectiveEndDt < now ? effectiveEndDt : now;

  let clampedDt: DateTime | null = null;

  if (calendarDt > effectiveEnd) {
    clampedDt = effectiveEnd;
  } else if (entityStart && calendarDt < entityStart) {
    clampedDt = entityStart;
  }

  if (clampedDt) {
    const currentTimeSet: string =
      (store.getState() as any).calendar?.hiveCalendar?.selectedTimeSet || 'day';

    let periodStart: DateTime;
    let periodEnd: DateTime;

    switch (currentTimeSet) {
      case 'month':
        periodStart = clampedDt.startOf('month');
        periodEnd = periodStart.plus({ months: 1 });
        break;
      case 'year':
        periodStart = clampedDt.startOf('year');
        periodEnd = periodStart.plus({ years: 1 });
        break;
      case 'week':
        periodStart = clampedDt.startOf('week');
        periodEnd = periodStart.plus({ weeks: 1 });
        break;
      default:
        periodStart = clampedDt;
        periodEnd = clampedDt.plus({ days: 1 });
        break;
    }

    dispatch(
      CALENDAR.setHiveDate({
        date: [periodStart.toJSDate(), periodEnd.toJSDate()],
        timeSet: currentTimeSet,
        setQueryParams: false,
      } as any)
    );
    // Sync URL silently via replaceState
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('dateFrom', periodStart.toFormat('yyyy-MM-dd'));
      url.searchParams.set('dateTo', periodEnd.toFormat('yyyy-MM-dd'));
      url.searchParams.set('timeSet', currentTimeSet);
      window.history.replaceState(window.history.state, '', url.toString());
    } catch {
      // no-op
    }
  }
}

export const CommunityResolver = () => {
  const dispatch = useDispatch();
  const params = useParams();
  const location = useLocation();
  const pathSegment = location.pathname.split('/')[1];

  async function CommunityInit() {
    if (!pathSegment?.includes('community')) {
      return;
    }

    const COMMUNITY_ID = params.id;

    if (!COMMUNITY_ID) {
      return;
    }

    let community: any = null;

    if (MOCK_ENTITIES) {
      // When mocking, fetch the mock JSON directly (can't rely on Redux
      // because EntitiesResolver runs in parallel and may not have finished)
      try {
        const mockUrl = `${window.location.origin}/entities.mock.json`;
        const res = await fetch(mockUrl);
        if (res.ok) {
          const mockData = await res.json();
          const communities = mockData.communities || mockData.community || [];
          community = communities.find(
            (c: any) => c.communityId === COMMUNITY_ID
          ) || null;
        }
      } catch (err) {
        console.warn('[CommunityResolver] Failed to load mock entities', err);
      }
    }

    if (!community) {
      // Token auto-injected by httpService interceptor
      [community] = await Promise.all([
        httpService
          .get({
            url: `${ENV.VITE_APP_BASE_URL}/community/catalog/v1_4/community/${COMMUNITY_ID}`,
            // /community/catalog/v1_4/community/{communityId}
          })
          .then((res: any) => {
            if (res.status === 200) {
              return res.data;
            } else {
              return null;
            }
          })
          .catch((err: any) => {
            dispatch(
              ALERT.SetAlert({
                value: {
                  type: 'BANNER_UNEXPECTED_ERROR',
                },
              } as any)
            );
            return null;
          }),
      ]);
    }

    dispatch(
      METADATA.SetSelectedEntity({
        selectedEntity: community,
        selectedEntityId: community?.communityId,
        entityType: 'community',
      } as any)
    );

    // One-shot clamp: if calendar dates are outside the community's valid range,
    // shift them to the nearest valid boundary. This runs once per navigation
    // (not in an effect loop) so it can't cause pushState spam.
    clampCalendarDateToEntity(community, dispatch);
  }

  useEffect(() => {
    CommunityInit();
  }, [params.id, pathSegment]);

  return <></>;
};

export const navigateToCommunity = (context: any, communityId: string) => {
  const queryParams = window?.location?.search || '';
  const dispatch = store.dispatch;

  const location = window.location;
  const pathSegment = location.pathname.split('/')[2];

  dispatch(METADATA.SetPrevSelectedEntityId(pathSegment as any));

  dispatch(KPI.ResetKpiFields());

  if (ENV?.VITE_APP_IS_WEB === 'true' && context === 'community') {
    dispatch(METADATA.ResetAllFilters());

    // queryParams = '';

    // const D = luxon.DateTime.now().startOf('day').toJSDate();
    // const D2 = luxon.DateTime.now().plus({ days: 1 }).startOf('day').toJSDate();

    // dispatch(
    //   CALENDAR.setHiveDate({
    //     date: [D, D2],
    //     timeSet: 'day',
    //     setQueryParams: false,
    //   } as any)
    // );
  }

  _navigate(`/${context}/${communityId}${queryParams}`);
};
